import { useState } from 'react'
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import config from '../config'
import { searchGroupBookings } from '../services/api'
import qualificationsIcon from '../assets/qualifications-1.png'

const Home = ({ companyData }) => {
  const navigate = useNavigate()
  const { company, profile, reviews, jobTypes, faqs } = companyData || {}

  const [selectedJob, setSelectedJob] = useState('')
  const [postcode, setPostcode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Helper to make image URLs absolute
  const getImageUrl = url => {
    if (!url) return null
    if (url.startsWith('/')) {
      return `${config.server}${url}`
    }
    return url
  }

  // Get the best available profile avatar image (trade profile image)
  const profileAvatarUrl =
    getImageUrl(profile?.avatar?.imageMedium) ||
    getImageUrl(profile?.avatar?.image) ||
    getImageUrl(profile?.avatar?.imageThumbnail)


  const handleGetDates = async () => {
    // Clear previous errors
    setError(null)

    // Validate job type is selected
    if (!selectedJob) {
      setError('Please select the job you need')
      return
    }

    // Validate postcode is entered
    if (!postcode.trim()) {
      setError('Please enter your postcode')
      return
    }

    // Basic UK postcode format validation (flexible)
    const postcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i
    if (!postcodeRegex.test(postcode.trim())) {
      setError('Please enter a valid UK postcode (e.g., SW1A 1AA)')
      return
    }

    setLoading(true)

    try {
      // Search for group bookings by postcode and job type
      // Use company.id from props (resolved from slug) instead of config.companyId (which is null for URL-based routing)
      const searchResults = await searchGroupBookings(
        company?.id || config.companyId,
        postcode,
        selectedJob
      )

      // Get the selected job type name for display
      const selectedJobType = jobTypes?.find(jt => jt.id === selectedJob)

      // Navigate to bookings page with search results
      navigate('/bookings', {
        state: {
          searchResults: searchResults,
          searchPostcode: postcode.trim().toUpperCase(),
          searchJobType: selectedJobType?.name,
          searchJobTypeId: selectedJob,
        },
      })
    } catch (err) {
      // Handle API errors
      if (err.response?.data?.error) {
        setError(err.response.data.error)
      } else if (err.response?.status === 404) {
        setError('Postcode not found. Please check and try again.')
      } else if (err.response?.status === 400) {
        setError('Invalid postcode. Please enter a valid UK postcode.')
      } else {
        setError('Unable to search for bookings. Please try again.')
      }
      console.error('Search error:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero Section - Two Column Layout */}
      <Box sx={{ bgcolor: '#e8f4f8', py: 8 }}>
        <Container maxWidth='lg'>
          <Grid container spacing={4} alignItems='center'>
            {/* Left Column - Form */}
            <Grid item xs={12} md={6}>
              {/* Company Name + Group Bookings */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant='h4'
                  sx={{
                    color: '#2c3e50',
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {company?.name || 'Add Detail ltd'}
                </Typography>
                <Typography
                  variant='h4'
                  sx={{
                    color: '#48eca2',
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  Group Bookings
                </Typography>
              </Box>

              {/* Tagline */}
              <Typography
                variant='h5'
                sx={{
                  color: '#2c3e50',
                  fontWeight: 700,
                  mb: 1,
                  lineHeight: 1.4,
                }}
              >
                Join a group booking in your area.
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: '#2c3e50',
                  mb: 3,
                  lineHeight: 1.6,
                }}
              >
                The more bookings on the day, the less you pay.
              </Typography>

              {/* Job Selector */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={selectedJob}
                  onChange={(e) => {
                    setSelectedJob(e.target.value)
                    setError(null) // Clear error when user selects a job
                  }}
                  displayEmpty
                  error={Boolean(error && !selectedJob)}
                  disabled={loading}
                  sx={{
                    bgcolor: '#ffffff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select the job you need
                  </MenuItem>
                  {jobTypes?.map(jobType => (
                    <MenuItem key={jobType.id} value={jobType.id}>
                      {jobType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Postcode Input */}
              <TextField
                fullWidth
                placeholder="E12 5SW"
                value={postcode}
                onChange={(e) => {
                  setPostcode(e.target.value)
                  setError(null) // Clear error when user types
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loading) {
                    handleGetDates()
                  }
                }}
                label="Your full postcode"
                error={Boolean(error)}
                disabled={loading}
                sx={{
                  mb: 2,
                  bgcolor: '#ffffff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                }}
              />

              {/* Error Message */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Get Dates Button */}
              <Button
                variant='contained'
                disabled={loading || !selectedJob || !postcode.trim()}
                sx={{
                  bgcolor: '#48eca2',
                  color: '#2c3e50',
                  '&:hover': { bgcolor: '#36d88f' },
                  '&:disabled': {
                    bgcolor: '#d0d0d0',
                    color: '#808080',
                  },
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                }}
                onClick={handleGetDates}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Searching...' : 'Get dates'}
              </Button>
            </Grid>

            {/* Right Column - Large Profile Image */}
            <Grid item xs={12} md={6}>
              {profileAvatarUrl && (
                <Box
                  sx={{
                    width: 350,
                    height: 350,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    mx: 'auto',
                    boxShadow: 'none',
                  }}
                >
                  <Box
                    component='img'
                    src={profileAvatarUrl}
                    alt={company?.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Main Content - Three Column Section */}
      <Container maxWidth='lg' sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Left Column - Company About */}
          <Grid item xs={12} md={4}>
            <Typography
              variant='h5'
              sx={{
                color: '#2c3e50',
                fontWeight: 700,
                mb: 3,
              }}
            >
              About us
            </Typography>
            {profile?.companyAbout && (
              <Typography
                variant='body2'
                sx={{
                  color: '#5a5c6e',
                  lineHeight: 1.8,
                  whiteSpace: 'pre-line',
                }}
              >
                {profile.companyAbout.length > 300
                  ? profile.companyAbout.slice(0, 300).trimEnd() + '...'
                  : profile.companyAbout}
              </Typography>
            )}
          </Grid>

          {/* Middle Column - Qualifications */}
          <Grid item xs={12} md={4}>
            <Box sx={{ bgcolor: '#f0f0f0', p: 3, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  component='img'
                  src={qualificationsIcon}
                  alt='Qualifications'
                  sx={{
                    width: 50,
                    height: 50,
                  }}
                />
                <Typography
                  variant='h6'
                  sx={{
                    color: '#2c3e50',
                    fontWeight: 700,
                  }}
                >
                  Qualifications
                </Typography>
              </Box>
              {profile?.qualificationsAbout && (
                <Box component='ul' sx={{ pl: 2, m: 0 }}>
                  {profile.qualificationsAbout.split('\n').filter(line => line.trim()).map((line, index) => (
                    <Typography
                      key={index}
                      component='li'
                      variant='body2'
                      sx={{
                        color: '#5a5c6e',
                        mb: 1,
                        lineHeight: 1.6,
                      }}
                    >
                      {line.trim()}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right Column - Insurance */}
          <Grid item xs={12} md={4}>
            <Box sx={{ bgcolor: '#f0f0f0', p: 3, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: '#48eca2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '1.5rem' }}>✓</Typography>
                </Box>
                <Typography
                  variant='h6'
                  sx={{
                    color: '#2c3e50',
                    fontWeight: 700,
                  }}
                >
                  Insurance
                </Typography>
              </Box>
              <Typography
                variant='body2'
                sx={{
                  color: '#5a5c6e',
                  lineHeight: 1.8,
                }}
              >
                {profile?.insuranceOther ||
                  `Public Liability Insurance. Limit of indemnity: £${
                    profile?.indemnityLimit || '1,000,000'
                  }. Valid until: ${profile?.validUntil || '1st Oct 2022'}`}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Recent Reviews Section */}
      {reviews && reviews.length > 0 && (
        <Box sx={{ bgcolor: '#e8f4f8', py: 6 }}>
          <Container maxWidth='lg'>
            <Typography
              variant='h4'
              sx={{
                color: '#2c3e50',
                fontWeight: 700,
                mb: 4,
              }}
            >
              Recent reviews
            </Typography>
            <Grid container spacing={3}>
              {reviews.slice(0, 4).map((review) => (
                <Grid item xs={12} sm={6} md={3} key={review.id || review.message}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant='subtitle1'
                          sx={{
                            fontWeight: 700,
                            color: '#2c3e50',
                            mb: 0.5,
                          }}
                        >
                          {review.customerName || 'Customer'}
                        </Typography>
                        {(review.reviewDate || review.createdAt) && (
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'text.secondary',
                              fontSize: '0.85rem',
                              mb: 0.5,
                            }}
                          >
                            {format(parseISO(review.reviewDate || review.createdAt), 'MMMM d, yyyy')}
                          </Typography>
                        )}
                        {review.jobTypeName && (
                          <Typography
                            variant='body2'
                            sx={{
                              color: 'primary.main',
                              fontWeight: 600,
                            }}
                          >
                            Service: {review.jobTypeName}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant='body2'
                        sx={{
                          color: '#5a5c6e',
                          mb: 2,
                          lineHeight: 1.6,
                        }}
                      >
                        {review.review || 'Great service!'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {[...Array(5)].map((_, i) => (
                          <Typography
                            key={i}
                            sx={{
                              color: i < (review.rating || 5) ? '#ffd700' : '#e0e0e0',
                              fontSize: '1.2rem',
                            }}
                          >
                            ★
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* FAQ Section */}
      <Container maxWidth='lg' sx={{ py: 6 }}>
        <Typography
          variant='h4'
          sx={{
            color: '#2c3e50',
            fontWeight: 700,
            mb: 4,
          }}
        >
          Some questions you might have
        </Typography>

        {faqs && faqs.length > 0 ? (
          faqs.map((faq) => (
            <Accordion key={faq.id}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: '#5a5c6e' }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  What happens on the boiler service?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: '#5a5c6e' }}>
                  Our qualified professionals will perform a comprehensive service according to
                  manufacturer guidelines and industry standards.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  How does group booking work?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: '#5a5c6e' }}>
                  We organize multiple bookings in the same area on the same day, reducing travel
                  costs. Savings are passed to you through group discounts.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography sx={{ fontWeight: 600, color: '#2c3e50' }}>
                  How much can I save?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: '#5a5c6e' }}>
                  Discounts increase with more bookings. Typical savings range from 10-30% off
                  standard prices.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Container>
    </Box>
  )
}

export default Home
